import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Checkbox, Box, MenuItem, Select } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { saveAs } from 'file-saver';
import { tableStyles, columnModel } from './TableStyle'; // Import stylów

const Table = () => {
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    nazwa: '',
    comments: '',
    weight: '',
    type: '',
    t: false,
  });
  const [open, setOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false); // Stan do zarządzania popupem usuwania

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedData = await fetchDataFromServer();
        setData(fetchedData);
      } catch (err) {
        console.error('Error fetching data', err);
      }
    };

    fetchData();
  }, []);

  const fetchDataFromServer = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
        operation: 'fetch',
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const fetchedData = (Array.isArray(response.data) ? response.data : []).map((row, index) => ({
        id: row.id || index, // Ensure each row has a unique id
        ...row,
      }));
      console.log('Fetched data:', fetchedData); // Logowanie danych
      return fetchedData;
    } catch (err) {
      console.error('Error fetching data', err);
      throw err; // Rzuć błąd, aby można było go obsłużyć w funkcji handleDownloadExcel
    }
  };

  const handleEditCellChange = async (newRow) => {
    const updatedRow = data.find((row) => row.id === newRow.id);
    if (updatedRow) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
          operation: 'update',
          id: newRow.id,
          data: newRow,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(data.map(row => (row.id === newRow.id ? newRow : row)));
      } catch (err) {
        console.error('Error updating data', err);
      }
    }
    return newRow;
  };

  const handleSelectChangeWithDate = async (id, field, value) => {
    const updatedRow = data.find((row) => row.id === id);
    if (updatedRow) {
      const updatedData = { 
        ...updatedRow,
         [field]: value,
         [`${field}_UpdatedAt`]: new Date().toLocaleString(),
         };
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
          operation: 'update',
          id,
          data: updatedData,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(data.map(row => (row.id === id ? updatedData : row)));
      } catch (err) {
        console.error('Error updating data', err);
      }
    }
  };

  const handleToggleCheckbox = async (id, field, value) => {
    const updatedRow = data.find((row) => row.id === id);
    if (updatedRow) {
      const updatedData = { 
        ...updatedRow,
        [field]: value,
      };
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
          operation: 'update',
          id,
          data: updatedData,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(prevData => prevData.map(row => 
          row.id === id ? { ...row, [field]: value } : row
        ));
      } catch (err) {
        console.error('Error updating data', err);
      }
    }
  };

  const handleSelectChange = async (id, field, value) => {
    const updatedRow = data.find((row) => row.id === id);
    if (updatedRow) {
      const updatedData = { 
        ...updatedRow,
         [field]: value,
         };
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
          operation: 'update',
          id,
          data: updatedData,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setData(data.map(row => (row.id === id ? updatedData : row)));
      } catch (err) {
        console.error('Error updating data', err);
      }
    }
  };

  const handleAddRowChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewRow({
      ...newRow,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAddRow = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
        operation: 'add',
        data: newRow,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Ensure the new row has a unique ID
      const newId = response.data.id || Date.now(); // Use server-provided ID or a timestamp as fallback
      const newRowWithId = { ...newRow, id: newId };

      setData([...data, newRowWithId]);
      setNewRow({
        nazwa: '',
        comments: '',
        weight: '',
        type: '',
        t: false,
      });
      setOpen(false);
    } catch (err) {
      console.error('Error adding row', err);
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
        operation: 'delete',
        id,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setData(data.filter(row => row.id !== id));
    } catch (err) {
      console.error('Error deleting data', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenTimestamp');
    window.location.replace(window.location.href.split('#')[0]);
  };

  const handleDeleteAllRows = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/database`, {
        operation: 'deleteAll',
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setData([]);
      setDeleteAllOpen(false);
    } catch (err) {
      console.error('Error deleting all data', err);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      // Pobierz aktualne dane z serwera
      const updatedData = await fetchDataFromServer();
      console.log('Updated data:', updatedData); // Logowanie danych

      if (updatedData.length === 0) {
        alert('No data available to export.');
        return;
      }

      // Wyślij dane do serwera w celu wygenerowania pliku Excel
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/save-excel`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Utwórz i zapisz plik Excel
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'data.xlsx');
    } catch (err) {
      console.error('Error downloading Excel file', err);
      alert(`Error downloading Excel file: ${err.message}`);
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }
  
    const formData = new FormData();
    formData.append('file', file);
  
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/import-excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          responseType: 'json',
        }
      );
  
      console.log('Server response:', response);
  
      // Sprawdzenie czy odpowiedź zawiera oczekiwane dane
      if (response.data?.message) {
        alert(response.data.message); // Wyświetl komunikat zwrócony przez serwer
  
        try {
          const fetchedData = await fetchDataFromServer();
          setData(fetchedData);
        } catch (fetchError) {
          console.error('Error fetching data after import', fetchError);
          alert('File imported, but error occurred while fetching data.');
        }
      } else {
        console.error('Unexpected response:', response.data);
        alert('Error importing file: Unexpected server response');
      }
    } catch (err) {
      console.error('Error importing Excel file', err);
      alert(`Error importing Excel file: ${err.message}`);
    }
  };  

  const getStageOptions = (type) => {
    switch (type) {
      case 'SZK':
        return ['H', 'U', 'S', 'US', 'C', 'CS', 'G', 'D', 'E'];
      case 'TAN':
        return ['300', '250', '200', '150', 'GRAT'];
      case 'ŚK':
        return ['40', '30', '25', 'GRAT', 'MY'];
      default:
        return [];
    }
  };

  const renderStageCell = (params) => {
    const options = getStageOptions(params.row.type);
    return (
      <Box>
        <Select
          value={params.value || ''}
          onChange={(event) => handleSelectChangeWithDate(params.id, params.field, event.target.value)}
          fullWidth
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>{option}</MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const columns = [
    { 
      field: 'nazwa', 
      headerName: 'Nazwa', 
      width: 200, 
      minWidth: 200, // Ustawienie minimalnej szerokości
      maxWidth: 200, // Ustawienie maksymalnej szerokości
      editable: true,
      cellClassName: 'MuiDataGrid-cell--stickyLeft',  // Klasa dla zablokowanej kolumny
      headerClassName: 'MuiDataGrid-columnHeader--stickyLeft',  // Klasa dla nagłówka zablokowanej kolumny
    },
    { field: 'comments', headerName: 'Comments', width: 120, editable: true },
    { field: 'weight', headerName: 'Weight', width: 80, editable: true },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 90, 
      //editable: false, 
      renderCell: (params) => (
        <Box>
          <Select
            value={params.value}
            onChange={(event) => {
              handleSelectChange(params.id, 'type', event.target.value)}}
            fullWidth
            disablePortal
            style={{ width: '100%' }}
          >
            <MenuItem value="SZK">SZK</MenuItem>
            <MenuItem value="TAN">TAN</MenuItem>
            <MenuItem value="ŚK">ŚK</MenuItem>
          </Select>
        </Box>
      )
    },
    { 
      field: 't', 
      headerName: 'T', 
      width: 50, 
      //editable: false, 
      type: 'boolean',
      renderCell: (params) => (
        <Box>
          <Checkbox 
            checked={Boolean(params.value)} 
            onChange={(event) => {
              console.log(`Checkbox changed: id=${params.id}, new value=${event.target.checked}`);
               handleToggleCheckbox(params.id, 't', event.target.checked)
              }
              } 
          />
        </Box>
      )
    },
    ...Array.from({ length: 20 }, (_, i) => ({ field: `stage1_${i + 1}`, headerName: `${i + 1}`, width: 90, editable: true, renderCell: renderStageCell })),
    ...Array.from({ length: 15 }, (_, i) => ({ field: `stage2_${i + 1}`, headerName: `${i + 1}`, width: 90, editable: true, renderCell: renderStageCell })),
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteRow(params.id)}
        />
      ]
    }
  ];

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <Box sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          rows={data}
          columns={columns}
          processRowUpdate={handleEditCellChange}
          columnGroupingModel={columnModel}
          sx={tableStyles.root} // Użycie stylów z osobnego pliku
          pinnedColumns={{ left: ['nazwa'] }} // Przypięcie pierwszej kolumny do lewej strony
        />
      </Box>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)} style={{ marginTop: 20 }}>
        Add Row
      </Button>
      <Button variant="contained" color="secondary" onClick={handleDownloadExcel} style={{ marginTop: 20, marginLeft: 20 }}>
        Download Excel
      </Button>
      <Button variant="contained" color="error" onClick={() => setDeleteAllOpen(true)} style={{ marginTop: 20, marginLeft: 20 }}>
        Delete All
      </Button>
      <input
        accept=".xlsx, .xls"
        style={{ display: 'none' }}
        id="import-excel-button"
        type="file"
        onChange={handleImportExcel}
      />
      <label htmlFor="import-excel-button">
        <Button variant="contained" color="primary" component="span" style={{ marginTop: 20, marginLeft: 20 }}>
          Import Excel
        </Button>
      </label>
      <Button variant="outlined" color="error" onClick={handleLogout} style={{ marginTop: 20, marginLeft: 20 }}>
        Wyloguj
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Add New Row</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="nazwa"
            label="Nazwa"
            type="text"
            fullWidth
            value={newRow.nazwa}
            onChange={handleAddRowChange}
          />
          <TextField
            margin="dense"
            name="comments"
            label="Comments"
            type="text"
            fullWidth
            value={newRow.comments}
            onChange={handleAddRowChange}
          />
          <TextField
            margin="dense"
            name="weight"
            label="Weight"
            type="text"
            fullWidth
            value={newRow.weight}
            onChange={handleAddRowChange}
          />
          <Select
            name="type"
            value={newRow.type}
            onChange={handleAddRowChange}
            fullWidth
          >
            <MenuItem value="SZK">SZK</MenuItem>
            <MenuItem value="TAN">TAN</MenuItem>
            <MenuItem value="ŚK">ŚK</MenuItem>
          </Select>
          <Checkbox
            name="t"
            checked={newRow.t}
            onChange={handleAddRowChange}
          /> T
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddRow} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteAllOpen} onClose={() => setDeleteAllOpen(false)}>
        <DialogTitle>Are you sure you want to delete all data?</DialogTitle>
        <DialogContent>
          This action cannot be undone. All data will be permanently deleted.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAllOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteAllRows} color="error">
            Delete All
          </Button>
          <Button onClick={handleDownloadExcel} color="primary">
            Download Current Version
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Table;